// ============================================
// RATTACHER UN DOSSIER À UN COMPTE CLIENT
// ============================================
// Un dossier ouvert par e-mail, par téléphone ou par un contact libre n'a
// pas toujours de compte client. Le personnel peut le rattacher à la main :
// POST /admin/tickets/:id/rattacher { customerId }. Le serveur refuse par
// un 409 (dossier déjà rattaché à un autre compte) ou un 400 (compte non
// utilisable) ; son message est affiché tel quel. Déjà rattaché à ce compte
// même : le serveur répond par un succès (idempotent).

import { AdminApi, ApiError } from '../../services/api';
import type { Ticket } from '../../types';

/** Le dossier n'a-t-il aucun compte client ? */
export const sansCompteClient = (t: Pick<Ticket, 'customerId' | 'customer'>): boolean =>
  !t.customerId && !t.customer;

/** Recherche proposée à l'ouverture : l'adresse du contact, sinon son nom. */
export function rechercheInitiale(t: Pick<Ticket, 'contactEmail' | 'contactName'>): string {
  const email = (t.contactEmail || '').split(/[;,]/)[0]?.trim() || '';
  return email || (t.contactName || '').trim();
}

/** Le serveur demande de confirmer : l'adresse du dossier n'est pas celle du compte. */
export function demandeConfirmationAdresse(erreur: unknown): erreur is ApiError {
  return erreur instanceof ApiError && erreur.statusCode === 409 && erreur.code === 'ADRESSE_DIFFERENTE';
}

/** Message à afficher après un refus : celui du serveur pour un 400 ou un 409. */
export function messageErreurRattachement(erreur: unknown): string {
  if (erreur instanceof ApiError && (erreur.statusCode === 400 || erreur.statusCode === 409) && erreur.message) {
    return erreur.message;
  }
  if (erreur instanceof ApiError && erreur.statusCode === 403) {
    return "Vous n'avez pas le droit de rattacher ce dossier.";
  }
  if (erreur instanceof ApiError && erreur.statusCode === 404) {
    return 'Dossier ou compte client introuvable.';
  }
  return erreur instanceof Error && erreur.message ? erreur.message : 'Le rattachement a échoué.';
}

/**
 * Rattache le dossier au compte puis relit la fiche complète (la réponse du
 * serveur ne porte pas forcément le client). Si la relecture échoue, le
 * rattachement est fait : on rend null et l'appelant recharge plus tard.
 */
export async function rattacherDossier(ticketId: string, customerId: string, confirmerAdresseDifferente = false): Promise<Ticket | null> {
  await AdminApi.rattacherClient(ticketId, customerId, confirmerAdresseDifferente);
  try {
    return await AdminApi.getTicketById(ticketId);
  } catch (e) {
    console.error('Relecture du dossier après rattachement :', e);
    return null;
  }
}
